import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core';
import { combineLatest, skip, BehaviorSubject } from 'rxjs';
import {
  map,
  filter,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataDisplayerModule } from 'src/app/components/data-displayer/data-displayer.module';
import { SelectComponent } from 'src/app/shared/components/select/select.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { DateRangeComponent } from 'src/app/shared/date-range/date-range.component';

import {
  AttributeEvolutionFacade,
  AttributeEvolutionState,
  IndicatorDto,
} from 'src/app/views/attribute-evolution/attribute-evolution.facade';
import { ChartConfigService } from 'src/app/views/attribute-evolution/chart-config.service';
import { AppLinkComponent } from 'src/app/shared/app-link/app-link.component';
import { EllipsisLoadingComponent } from 'src/app/shared/components/ellipsis-loading/ellipsis-loading.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-attribute-evolution',
    templateUrl: './attribute-evolution.component.html',
    styleUrls: ['./attribute-evolution.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        DataDisplayerModule,
        SelectComponent,
        SharedModule,
        AppLinkComponent,
        DateRangeComponent,
        EllipsisLoadingComponent,
    ],
    providers: [AttributeEvolutionFacade]
})
export class AttributeEvolutionComponent implements OnInit {
  readonly unavailableText = $localize`:@@js_unavailable:indisponible`;
  readonly loadingIndicatorsText = $localize`:@@loadingIndicators:Cargando indicadores...`;

  @Input() planId: string = '';
  @Input() documentNumber: string = '';
  @Input() registryId: string = '';
  @Input() inclusionDate: Date | null = null;
  @Output() backToList = new EventEmitter<void>();

  readonly personType = this.globalService.getPersonTypeCurrentValue();

  readonly startDate$ = new BehaviorSubject<Date | null>(null);
  readonly endDate$ = new BehaviorSubject<Date | null>(null);

  private cachedChartOptions: any = {};
  private lastChartDataLength = 0;
  private lastSelectedIndicatorName = '';
  private lastIndicatorValues = '';

  readonly vm$ = combineLatest([
    this.facade.state$,
    this.startDate$,
    this.endDate$,
  ]).pipe(
    map(([facade, startDate, endDate]) => {
      const chartDataValues = facade.chartData?.data.values || [];

      let chartOptions = null;
      if (chartDataValues.length > 0) {
        chartOptions = this.getChartOptionsOptimized(facade, chartDataValues);
      }
      return {
        facade,
        dateRange: {
          startDate,
          endDate,
          minDate: this.inclusionDate,
          maxDate: new Date(),
          isValid: !!(startDate && endDate),
        },
        chartOptions,
        chartDataValues,
        personType: this.personType,
        isLoading: facade.isLoadingIndicators || facade.isLoadingChart,
        hasError: facade.error != null,
        canLoadData: !!(facade.selectedIndicator && startDate && endDate),
      };
    })
  );

  constructor(
    private readonly facade: AttributeEvolutionFacade,
    private readonly chartConfigService: ChartConfigService,
    private readonly globalService: GlobalServiceService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.facade.loadIndicators(this.planId);
    this.globalService
      .getPersonType$()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onBackClick();
      });

    this.setupReactiveDataLoading();
  }

  onIndicatorSelect(selection: IndicatorDto): void {
    this.facade.setSelectedIndicator(selection);
  }

  onStartDateChange(date: Date): void {
    if (!date) return;
    this.startDate$.next(date);
  }

  onEndDateChange(date: Date): void {
    if (!date) return;
    this.endDate$.next(date);
  }

  onBackClick(): void {
    this.backToList.emit();
  }

  private setupReactiveDataLoading(): void {
    combineLatest([
      this.facade.state$.pipe(map((s) => s.selectedIndicator)),
      this.startDate$,
      this.endDate$,
      this.facade.state$.pipe(map((s) => s.isLoadingChart)),
      this.facade.state$.pipe(map((s) => s.isLoadingIndicators)),
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(200),
        filter(
          ([indicator, start, end, isLoadingChart, isLoadingIndicators]) => {
            const ready = !!(
              indicator &&
              start &&
              end &&
              !isLoadingChart &&
              !isLoadingIndicators
            );

            if (!ready) return false;
            return true;
          }
        ),
        distinctUntilChanged(
          ([prevInd, prevStart, prevEnd], [currInd, currStart, currEnd]) =>
            prevInd?.id === currInd?.id &&
            prevStart?.getTime?.() === currStart?.getTime?.() &&
            prevEnd?.getTime?.() === currEnd?.getTime?.()
        )
      )
      .subscribe(([indicator, start, end]) => {
        this.loadChartDataReactive(indicator, start, end);
      });
  }

  private loadChartDataReactive(indicator: any, start: Date, end: Date): void {
    if (!indicator || !start || !end) return;
    this.facade.loadChartData(this.registryId, indicator.id ?? '', start, end);
  }

  private generateChartOptions(state: AttributeEvolutionState): any {
    const data = this.extractChartValues(state);

    if (data.length === 0) {
      return {};
    }

    const baseOptions = this.chartConfigService.generateChartOptions(
      data,
      state.selectedIndicator?.name
    );

    return {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        title: { display: false },
        legend: {
          display: true,
          position: 'bottom' as const,
        },
      },
      elements: {
        point: {
          radius: 4.5,
          hoverRadius: 7,
          hitRadius: 12,
          backgroundColor: '#42A5F5',
          borderColor: '#ffffff',
          borderWidth: 2,
        },
        line: {
          tension: 0.1,
          borderWidth: 4,
          borderColor: '#42A5F5',
        },
      },
    };
  }

  private getChartOptionsOptimized(
    state: AttributeEvolutionState,
    chartDataValues: number[]
  ): any {
    const currentIndicatorName = state.selectedIndicator?.name || '';
    const currentDataLength = chartDataValues.length;
    const currentIndicatorValues = state.chartData.data.values.join();

    if (
      currentDataLength > 0 &&
      (this.lastChartDataLength !== currentDataLength ||
        this.lastSelectedIndicatorName !== currentIndicatorName ||
        this.lastIndicatorValues !== currentIndicatorValues ||
        Object.keys(this.cachedChartOptions).length === 0)
    ) {
      this.cachedChartOptions = this.generateChartOptions(state);
      this.lastChartDataLength = currentDataLength;
      this.lastSelectedIndicatorName = currentIndicatorName;
      this.lastIndicatorValues = currentIndicatorValues;
    }

    return this.cachedChartOptions;
  }

  private extractChartValues(state: AttributeEvolutionState): number[] {
    if (state.chartData.data.values.length > 0 && state.chartData.data.values) {
      return state.chartData.data.values;
    }
    return [];
  }

  optionLabelFn = (indicator: any) => indicator?.name ?? '';
}
